/**
 * What a chat composer keeps per conversation for this browser session
 * (chat-relay-reply D5, D10; client-chat-screen D7). One store for both skins: the
 * admin keys it by client (and room), the client screen by its own user. Storage can
 * throw (private mode, quota), so every access fails soft: a lost draft never breaks
 * the screen.
 *
 * - The draft: the editable words in the pill.
 * - Unsent messages: words already sent, with the idempotency key they were sent
 *   under, until a server answer carries the message. Kept apart from the draft so
 *   leaving and returning re-sends them under the SAME key, never as a new message.
 */
const draftKey = (scope: string) => `telify:chat-draft:${scope}`;
const unsentKey = (scope: string) => `telify:chat-unsent:${scope}`;

/** The server's bound on a message after trimming (D1), counted the same way. */
export const REPLY_MAX_CHARS = 4096;

export function readDraft(scope: string): string {
  try {
    return sessionStorage.getItem(draftKey(scope)) ?? '';
  } catch {
    return '';
  }
}

/** An empty draft removes the entry. */
export function writeDraft(scope: string, text: string): void {
  try {
    if (text) sessionStorage.setItem(draftKey(scope), text);
    else sessionStorage.removeItem(draftKey(scope));
  } catch {
    // Draft persistence is best effort.
  }
}

export interface UnsentReply {
  key: string;
  text: string;
  at: string;
}

export function readUnsent(scope: string): UnsentReply[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(unsentKey(scope)) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((item): item is UnsentReply =>
        typeof item?.key === 'string' && typeof item?.text === 'string' && typeof item?.at === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeUnsent(scope: string, list: UnsentReply[]): void {
  try {
    if (list.length > 0) sessionStorage.setItem(unsentKey(scope), JSON.stringify(list));
    else sessionStorage.removeItem(unsentKey(scope));
  } catch {
    // Best effort, like the draft.
  }
}

export function rememberUnsent(scope: string, reply: UnsentReply): void {
  writeUnsent(scope, [...readUnsent(scope).filter((item) => item.key !== reply.key), reply]);
}

/** The server holds these keys now: they are no longer unsent. */
export function forgetUnsent(scope: string, keys: ReadonlySet<string>): void {
  const list = readUnsent(scope);
  const kept = list.filter((item) => !keys.has(item.key));
  if (kept.length !== list.length) writeUnsent(scope, kept);
}
